/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetOrganizationUserSessionIssuerAuthenticationHostImpactRequest,
  GetOrganizationUserSessionIssuerAuthenticationHostImpactSecurity,
} from "../models/operations/getorganizationusersessionissuerauthenticationhostimpact.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOrganizationUserSessionIssuerAuthenticationHostImpactQuery,
  OrganizationUserSessionIssuerAuthenticationHostImpactQueryData,
  prefetchOrganizationUserSessionIssuerAuthenticationHostImpact,
  queryKeyOrganizationUserSessionIssuerAuthenticationHostImpact,
} from "./organizationUserSessionIssuerAuthenticationHostImpact.core.js";
export {
  buildOrganizationUserSessionIssuerAuthenticationHostImpactQuery,
  type OrganizationUserSessionIssuerAuthenticationHostImpactQueryData,
  prefetchOrganizationUserSessionIssuerAuthenticationHostImpact,
  queryKeyOrganizationUserSessionIssuerAuthenticationHostImpact,
};

export type OrganizationUserSessionIssuerAuthenticationHostImpactQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getAuthenticationHostImpact organizationUserSessionIssuers
 *
 * @remarks
 * Preview moving a user_session_issuer in the caller's organization onto or off the deployment's authentication host: where clients sign in before and after, and how many MCP servers that affects. Requires org:read.
 */
export function useOrganizationUserSessionIssuerAuthenticationHostImpact(
  request: GetOrganizationUserSessionIssuerAuthenticationHostImpactRequest,
  security?:
    | GetOrganizationUserSessionIssuerAuthenticationHostImpactSecurity
    | undefined,
  options?: QueryHookOptions<
    OrganizationUserSessionIssuerAuthenticationHostImpactQueryData,
    OrganizationUserSessionIssuerAuthenticationHostImpactQueryError
  >,
): UseQueryResult<
  OrganizationUserSessionIssuerAuthenticationHostImpactQueryData,
  OrganizationUserSessionIssuerAuthenticationHostImpactQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildOrganizationUserSessionIssuerAuthenticationHostImpactQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getAuthenticationHostImpact organizationUserSessionIssuers
 *
 * @remarks
 * Preview moving a user_session_issuer in the caller's organization onto or off the deployment's authentication host: where clients sign in before and after, and how many MCP servers that affects. Requires org:read.
 */
export function useOrganizationUserSessionIssuerAuthenticationHostImpactSuspense(
  request: GetOrganizationUserSessionIssuerAuthenticationHostImpactRequest,
  security?:
    | GetOrganizationUserSessionIssuerAuthenticationHostImpactSecurity
    | undefined,
  options?: SuspenseQueryHookOptions<
    OrganizationUserSessionIssuerAuthenticationHostImpactQueryData,
    OrganizationUserSessionIssuerAuthenticationHostImpactQueryError
  >,
): UseSuspenseQueryResult<
  OrganizationUserSessionIssuerAuthenticationHostImpactQueryData,
  OrganizationUserSessionIssuerAuthenticationHostImpactQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOrganizationUserSessionIssuerAuthenticationHostImpactQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setOrganizationUserSessionIssuerAuthenticationHostImpactData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      useAuthenticationHost: boolean;
      gramSession?: string | undefined;
    },
  ],
  data: OrganizationUserSessionIssuerAuthenticationHostImpactQueryData,
): OrganizationUserSessionIssuerAuthenticationHostImpactQueryData | undefined {
  const key = queryKeyOrganizationUserSessionIssuerAuthenticationHostImpact(
    ...queryKeyBase,
  );

  return client.setQueryData<
    OrganizationUserSessionIssuerAuthenticationHostImpactQueryData
  >(key, data);
}

export function invalidateOrganizationUserSessionIssuerAuthenticationHostImpact(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      useAuthenticationHost: boolean;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationUserSessionIssuers",
      "getAuthenticationHostImpact",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllOrganizationUserSessionIssuerAuthenticationHostImpact(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationUserSessionIssuers",
      "getAuthenticationHostImpact",
    ],
  });
}
